import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO/SEO';

import { useParams, Link } from 'react-router-dom';
import { blogService } from '../../services/blogService';
import './BlogPost.css';

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Helper function to parse content string from database
    const parseContentString = (contentStr) => {
        if (!contentStr) return [];
        
        try {
            if (Array.isArray(contentStr)) return contentStr;
            if (typeof contentStr === 'string' && contentStr.trim().startsWith('[')) {
                return JSON.parse(contentStr);
            }
            
            const lines = contentStr.split('\n');
            const content = [];
            
            lines.forEach(line => {
                line = line.trim();
                if (!line) return;
                
                if (line.startsWith('## ')) {
                    content.push({ type: 'heading', text: line.substring(3) });
                } else if (line.startsWith('- ')) {
                    const lastItem = content[content.length - 1];
                    if (lastItem && lastItem.type === 'list') {
                        lastItem.items.push(line.substring(2));
                    } else {
                        content.push({ type: 'list', items: [line.substring(2)] });
                    }
                } else {
                    content.push({ type: 'paragraph', text: line });
                }
            });
            
            return content;
        } catch (error) {
            console.error('Error parsing content:', error);
            return [{ type: 'paragraph', text: contentStr }];
        }
    };

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await blogService.getPostBySlug(slug);
                
                if (response.success && response.data) {
                    const foundPost = {
                        ...response.data,
                        heroImage: response.data.hero_image,
                        content: parseContentString(response.data.content)
                    };
                    setPost(foundPost);
                    
                    // Fetch related posts
                    const allPostsResponse = await blogService.getAllPosts();
                    if (allPostsResponse.success && allPostsResponse.data) {
                        const related = allPostsResponse.data
                            .filter(p => p.slug !== slug)
                            .slice(0, 3);
                        setRelatedPosts(related);
                    }
                } else {
                    setPost(null);
                }
                
                setLoading(false);
            } catch (error) {
                console.error('Error loading blog post:', error);
                setPost(null);
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    if (loading) {
        return (
            <div className="hubspot-blog-single-wrap">
            <SEO 
                title="Blog Post Services | Digi Network" 
                description="Learn more about our Blog Post services to grow your business online with Digi Network." 
                canonical="/blog/blogpost" 
            />

                <div className="loading-state">Loading...</div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="hubspot-blog-single-wrap">
                <div className="not-found-state">
                    <h1>Post Not Found</h1>
                    <p>The blog post you're looking for doesn't exist.</p>
                    <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
    };

    const formatFullDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="hubspot-blog-single-wrap">
            {/* Breadcrumb Navigation */}
            <div className="hubspot-breadcrumb-container">
                <nav className="hubspot-breadcrumb">
                    <Link to="/" className="breadcrumb-item">Home</Link>
                    <span className="breadcrumb-separator">›</span>
                    <Link to="/blog" className="breadcrumb-item">Blog</Link>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-current">{post.title}</span>
                </nav>
            </div>

            {/* Hero Section */}
            <div className="hubspot-hero-section">
                <div className="hubspot-hero-content">
                    <div className="hubspot-hero-left">
                        <h1 className="hubspot-main-title">{post.title}</h1>
                        <div className="hubspot-author-byline">
                            <span>Written by: </span>
                            <span className="author-link">{post.author}</span>
                        </div>
                    </div>
                    
                    <div className="hubspot-hero-right">
                        {post.heroImage ? (
                            <div className="hubspot-hero-image">
                                <img src={post.heroImage} alt={post.title} className="hero-featured-image" />
                            </div>
                        ) : (
                            <div className="podzay-hero-graphic">
                                <div className="podzay-graphic-bg">
                                    <div className="podzay-illustration">
                                        <div className="placeholder-icon">📱</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="hubspot-updated-date">
                    <span>Updated: {formatDate(post.date)}</span>
                </div>
            </div>

            {/* Top CTA Box */}
            <div className="podzay-cta-box">
                <div className="podzay-cta-content">
                    <div className="podzay-cta-left">
                        <div className="podzay-cta-icon">
                            <div className="cta-icon-placeholder">🚀</div>
                        </div>
                    </div>
                    <div className="podzay-cta-right">
                        <h3 className="cta-title">START YOUR DIGITAL TRANSFORMATION</h3>
                        <p className="cta-description">Boost your online presence with our powerful digital marketing strategies.</p>
                        <Link to="/contact" className="podzay-cta-button">
                            Get Started Today
                        </Link>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="hubspot-content-wrapper">
                <div className="hubspot-main-content">
                    {post.content && post.content.map((block, index) => {
                        switch (block.type) {
                            case 'heading':
                                return <h2 key={index}>{block.text}</h2>;
                            case 'paragraph':
                                return <p key={index}>{block.text}</p>;
                            case 'list':
                                return (
                                    <ul key={index}>
                                        {block.items.map((item, idx) => (
                                            <li key={idx}>{item}</li>
                                        ))}
                                    </ul>
                                );
                            default:
                                return null;
                        }
                    })}
                </div>

                {/* Bottom CTA */}
                <div className="podzay-bottom-cta">
                    <div className="podzay-bottom-cta-bg">
                        <div className="podzay-bottom-content">
                            <div className="podzay-bottom-left">
                                <div className="podzay-laptop-icon">💼</div>
                            </div>
                            <div className="podzay-bottom-right">
                                <span className="podzay-cta-label">DIGITAL MARKETING SERVICES</span>
                                <h3 className="podzay-bottom-title">Join Our Success Stories</h3>
                                <p className="podzay-bottom-description">Partner with us to elevate your brand, drive more traffic, and achieve measurable results with our comprehensive digital marketing solutions.</p>
                                <Link to="/contact" className="podzay-bottom-button">
                                    Contact Us Now
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Topics Tags */}
                {post.tags && post.tags.length > 0 && (
                    <div className="hubspot-topics-section">
                        <span className="topics-label">Topics:</span>
                        <div className="hubspot-tags">
                            {post.tags.map((tag, index) => (
                                <span key={index} className="topic-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Social Share */}
                <div className="hubspot-social-share">
                    <h3 className="share-title">Don't forget to share this post!</h3>
                    <div className="social-share-buttons">
                        <a 
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                            className="share-btn twitter-btn" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Share on Twitter"
                        >
                            <span className="share-icon">𝕏</span>
                        </a>
                        <a 
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                            className="share-btn facebook-btn" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Share on Facebook"
                        >
                            <span className="share-icon">f</span>
                        </a>
                        <a 
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                            className="share-btn linkedin-btn" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Share on LinkedIn"
                        >
                            <span className="share-icon">in</span>
                        </a>
                        <a 
                            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`}
                            className="share-btn whatsapp-btn" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Share on WhatsApp"
                        >
                            WhatsApp
                        </a>
                        <a 
                            href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(window.location.href)}`}
                            className="share-btn email-btn" 
                            aria-label="Share via Email"
                        >
                            ✉️
                        </a>
                    </div>
                </div>

                {/* Related Articles Section */}
                {relatedPosts.length > 0 && (
                    <div className="hubspot-related-articles">
                        <h2 className="related-title">Related Articles</h2>
                        <div className="related-articles-grid">
                            {relatedPosts.map((relatedPost) => (
                                <div key={relatedPost.id} className="related-article-card">
                                    <div className="related-article-image">
                                        {relatedPost.hero_image ? (
                                            <Link to={`/blog/${relatedPost.slug}`}>
                                                <img src={relatedPost.hero_image} alt={relatedPost.title} className="related-img" />
                                            </Link>
                                        ) : (
                                            <div className="related-placeholder-img">
                                                <div className="placeholder-bg"></div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="related-article-content">
                                        <h3 className="related-article-title">
                                            <Link to={`/blog/${relatedPost.slug}`}>{relatedPost.title}</Link>
                                        </h3>
                                        <div className="related-article-date">
                                            {formatFullDate(relatedPost.date)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogPost;
